import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';
import { Spinner, type SpinnerProps } from './spinner';

const buttonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center gap-2 rounded-md text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25 disabled:cursor-not-allowed disabled:opacity-disabled',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        secondary:
          'bg-neutral-200 text-secondary-foreground hover:bg-neutral-300 dark:bg-neutral-600 dark:hover:bg-neutral-500',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-primary text-primary bg-transparent hover:bg-primary/10',
        'secondary-outline':
          'border border-border-strong text-secondary-foreground bg-transparent hover:bg-secondary/20',
        'destructive-outline':
          'border border-destructive text-destructive bg-transparent hover:bg-destructive/10',
      },
      size: {
        max: 'h-control-md w-full px-control-x py-control-y-sm',
        lg: 'h-control-md px-control-x py-control-y-sm',
        md: 'h-control-base px-control-x-sm',
        sm: 'h-control-sm px-control-x-xs text-xs',
        xs: 'h-control-xs px-control-y-sm text-xs',
        icon: 'h-control-base w-control-base',
        'icon-xs': 'h-control-xs w-control-xs',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'lg',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const spinnerColorByVariant: Record<NonNullable<ButtonProps['variant']>, SpinnerProps['color']> = {
  default: 'inverse',
  secondary: 'inverse',
  destructive: 'inverse',
  outline: 'primary',
  'secondary-outline': 'default',
  'destructive-outline': 'default',
};

const spinnerSizeByButtonSize: Record<NonNullable<ButtonProps['size']>, SpinnerProps['size']> = {
  max: 'sm',
  lg: 'sm',
  md: 'sm',
  sm: 'sm',
  xs: 'sm',
  icon: 'sm',
  'icon-xs': 'sm',
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const resolvedVariant = variant ?? 'default';
    const resolvedSize = size ?? 'lg';
    const spinnerColor = spinnerColorByVariant[resolvedVariant] ?? 'inverse';
    const spinnerSize = spinnerSizeByButtonSize[resolvedSize] ?? 'sm';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }), loading && 'relative')}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? (
          <>
            <Spinner
              className="pointer-events-none absolute"
              size={spinnerSize}
              color={spinnerColor}
            />
            <span className="opacity-0">{children}</span>
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
